import styled from 'styled-components';

import { SaveButton } from '../Button/SaveButton';

import {
  Banner,
  BannerHeading,
  BannerIcon,
  Box,
  Button,
  Form,
  ModalBody,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spacer,
} from '@aircall/tractor-v2';
import { getSavingStatus } from '@components/ModalFormV2/utils/getSavingStatus';
import { AnimatePresence } from 'framer-motion';
import { Form as RRForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { ModalFormProps } from '.';

const StyledForm = styled(Form)`
  display: flex;
  height: 100%;
  flex-direction: column;
`;

export function ModalForm<FormValues, InitialFormValues = FormValues>({
  show,
  formProps,
  onHide,
  onCancelClick = onHide,
  title,
  children,
  submitButtonText,
  size = 'regular',
  allowPristineSubmit = false,
  'data-tracking': dataTracking,
  extraFooterContent,
}: ModalFormProps<FormValues, InitialFormValues>) {
  const { t } = useTranslation();

  return (
    <ModalDialog show={show} onHide={onHide} size={size}>
      <RRForm<FormValues, InitialFormValues> {...formProps}>
        {(formRenderProps) => {
          const { handleSubmit, hasValidationErrors, pristine, submitError, submitting } =
            formRenderProps;
          const shouldDisableSubmit =
            submitting || hasValidationErrors || (!allowPristineSubmit && pristine);

          return (
            <StyledForm onSubmit={handleSubmit}>
              <ModalHeader>
                <ModalTitle>{title}</ModalTitle>
              </ModalHeader>
              <ModalBody p={0}>{children(formRenderProps)}</ModalBody>
              <AnimatePresence>
                {submitError && (
                  <Banner variant='critical' data-test='banner-wrapper'>
                    <BannerIcon />
                    <BannerHeading>{t(submitError)}</BannerHeading>
                  </Banner>
                )}
              </AnimatePresence>
              <ModalFooter>
                <Spacer space='xxs' alignItems='center' justifyContent='space-between'>
                  {extraFooterContent && <Box>{extraFooterContent}</Box>}
                  <Button
                    onClick={onCancelClick}
                    type='button'
                    mode='ghost'
                    variant='secondary'
                    data-test='modal-form-cancel-button'
                  >
                    {t('app.form.buttons.cancel')}
                  </Button>
                  <SaveButton
                    type='submit'
                    data-test='modal-form-submit-button'
                    disabled={shouldDisableSubmit}
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    {...(dataTracking && !shouldDisableSubmit && { 'data-tracking': dataTracking })}
                    status={getSavingStatus(formRenderProps)}
                    content={submitButtonText || t('app.form.buttons.submit')}
                  />
                </Spacer>
              </ModalFooter>
            </StyledForm>
          );
        }}
      </RRForm>
    </ModalDialog>
  );
}
